import { UnifiedPoolData } from 'types/analytics'

type PoolLike = {
  token0: { symbol: string; name: string }
  token1: { symbol: string; name: string }
}

const normalizeSearchValue = (value?: string): string => (value ?? '').toLowerCase().replace(/\u20ae/g, 't') // map ₮ to t to support tokens like USD₮

export const isAddress = (value: string): boolean => /^0x[0-9a-fA-F]{40}$/.test(value.trim())

export const extractPairTerms = (value: string): string[] =>
  normalizeSearchValue(value)
    .split(/[\s/\-_]+/)
    .filter((term) => term.length > 0)

export const matchesPoolPair = (pool: PoolLike, searchValue: string): boolean => {
  const pairTerms = extractPairTerms(searchValue)
  if (pairTerms.length !== 2) return false

  const token0Name = normalizeSearchValue(pool.token0?.name)
  const token0Symbol = normalizeSearchValue(pool.token0?.symbol)
  const token1Name = normalizeSearchValue(pool.token1?.name)
  const token1Symbol = normalizeSearchValue(pool.token1?.symbol)

  const matchesToken0 = (term: string) => token0Name.includes(term) || token0Symbol.includes(term)
  const matchesToken1 = (term: string) => token1Name.includes(term) || token1Symbol.includes(term)

  return (
    (matchesToken0(pairTerms[0]) && matchesToken1(pairTerms[1])) ||
    (matchesToken0(pairTerms[1]) && matchesToken1(pairTerms[0]))
  )
}

export const poolMatchesQuery = (pool: UnifiedPoolData, query: string): boolean => {
  const trimmed = query.trim()
  if (trimmed.length === 0) return true

  const normalizedQuery = normalizeSearchValue(trimmed)

  if (normalizedQuery.startsWith('0x')) {
    return pool.address.toLowerCase().includes(normalizedQuery)
  }

  if (isAddress(trimmed)) {
    return pool.address.toLowerCase().includes(normalizedQuery)
  }

  const pairTerms = extractPairTerms(trimmed)
  if (pairTerms.length === 2) {
    return matchesPoolPair(pool, trimmed)
  }

  return (
    normalizeSearchValue(pool.token0.symbol).includes(normalizedQuery) ||
    normalizeSearchValue(pool.token1.symbol).includes(normalizedQuery) ||
    normalizeSearchValue(pool.token0.name).includes(normalizedQuery) ||
    normalizeSearchValue(pool.token1.name).includes(normalizedQuery)
  )
}
